import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import appStore from '@/store/app';
import erpStore from '@/store/erp';
import authenticator from '@/store/authentication';
import CardComponent from '@/components/CardComponent';
import ListToolbar from '@/components/ListToolbar';
import { MarketAd, MarketAdType, SelectItem } from '@/types';
import { debounce } from '@/core/debounce';
import { MarketAdsSearch } from '@/models';
// import tracer from '@/core/tracer';

@Component({
    components: { CardComponent, ListToolbar },
})
export default class ErpMarket extends Vue {

    @Prop({ default: true }) private readonly checkable!: boolean;

    private selected: MarketAd | null = null;

    private keyword = this.search.keyword;
    private adType = this.search.adType;
    private currentPage = this.search.pageNumber;
    private pageSize = this.search.pageSize;

    private readonly minLength = 3;

    private debouncedFetchMarketAdsAsync = async (): Promise<void> => undefined;

    get loading(): boolean {
        return appStore.isLoading;
    }

    get ads(): MarketAd[] {
        return erpStore.marketAds;
    }

    get total(): number {
        return erpStore.totalMarketAds;
    }

    get search(): MarketAdsSearch {
        return erpStore.marketAdsSearch;
    }

    get filters(): SelectItem<number>[] {
        const result: SelectItem<number>[] = [];
        result.push({ value: MarketAdType.ALL, description: 'Tutti'} as SelectItem<number>);
        result.push({ value: MarketAdType.RC, description: 'Ricerche'} as SelectItem<number>);
        result.push({ value: MarketAdType.OF, description: 'Offerte'} as SelectItem<number>);
        result.push({ value: MarketAdType.PR, description: 'Lavoro'} as SelectItem<number>);
        result.push({ value: MarketAdType.VA, description: 'Varie'} as SelectItem<number>);
        result.push({ value: MarketAdType.AL, description: 'Allarmi'} as SelectItem<number>);
        return result;
    }

    get canRemove(): boolean {
        if (!this.selected) return false;
        if (this.selected.partnerCode === authenticator.userProfile.aid) return true;
        return false;
    }

    get canEdit(): boolean {
        if (!this.selected) return false;
        if (this.selected.partnerCode === authenticator.userProfile.aid) return true;
        return false;
    }

    private async created() {
        this.debouncedFetchMarketAdsAsync = debounce(this.fetchMarketAdsAsync, 600);
        if (!this.ads || this.total === 0) {
            await this.fetchMarketAdsAsync();
        }
    }

    private async mounted() {
        await appStore.activateMenuById({rootId: 200, subMenuId: 201 });
    }

    @Watch('keyword', { immediate: false, deep: false })
    private async onKeywordChanged(value: string, previousValue?: string) {

        // tracer('onKeywordChanged', [value, previousValue]);

        if (previousValue  === undefined) return;
        if (value.trim() === '' || value.trim().length >= this.minLength) {
            this.search.keyword = value;
            this.search.pageNumber = 1;
            await this.debouncedFetchMarketAdsAsync();
        }
    }

    @Watch('adType', { immediate: false, deep: false })
    private async onAdTypeChanged(value: number, previousValue?: number) {
        if (previousValue === undefined) return;

        // tracer('onAdTypeChanged', [value, previousValue]);

        this.search.adType = value;
        this.search.pageNumber = 1;

        await this.fetchMarketAdsAsync();
    }

    private async addAd() {
        await this.$router.push({ name: 'market', params: { id: '0' } });
    }

    private async editAd(marketAd: MarketAd) {

        if (!this.canEdit) return;

        const selectedId: number = marketAd ? marketAd.id : (this.selected as MarketAd).id;

        await this.$router.push({ name: 'market', params: { id: selectedId.toString() } });
    }

    private async deleteAd() {
        if (!this.canRemove) { return; }

        const selectedId: number = this.selected!.id;

        this.$buefy.dialog.confirm({
            title: 'Eliminazione Annuncio',
            message: 'Sei sicuro/a di voler <b>rimuovere</b> l\'annuncio selezionato ?',
            confirmText: 'Conferma',
            cancelText: 'Annulla',
            type: 'is-warning',
            hasIcon: true,
            onConfirm: async () => {
                await erpStore.deleteMarketAdAsync({ id: selectedId });
            },
        });
    }

    private async onPageChange(page: number) {
        this.search.pageNumber = page;
        await this.fetchMarketAdsAsync();
    }

    private select(row: MarketAd, oldRow: MarketAd | null): void {
        this.selected = row;
    }

    private async reload() {
        this.search.pageNumber = 1;
        await this.fetchMarketAdsAsync();
    }

    private async fetchMarketAdsAsync() {
        await erpStore.fetchMarketAdsAsync(this.search);
    }
}
